import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  ADMIN_LINKS,
  INSPECTOR_LINKS,
  STUDENT_LINKS,
  TEACHER_LINKS,
} from '../../../constants';
import { Space } from '../../access/space';
import { StudentFeatureService } from '../student-feature.service';
import { AuthService } from './auth.service';
import { SessionTokenService } from './session-token.service';

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  private sidebarOpen$ = new BehaviorSubject<boolean>(false);
  private linksSubject$ = new BehaviorSubject<any[]>([]);
  links$ = this.linksSubject$.asObservable();

  /** L'espace du dernier menu composé : c'est lui qu'on recompose quand les droits changent. */
  private composedFor: Space | null | undefined;
  private composed = false;

  private readonly studentFeatures = inject(StudentFeatureService);

  constructor(private authService: AuthService) {
    // Un jeton renouvelé peut porter d'autres droits — un profil attribué ou
    // retiré entre-temps. Le menu ne se composait qu'au changement d'espace :
    // il continuait de proposer une porte fermée, ou de taire une porte
    // ouverte, jusqu'au rechargement de la page.
    inject(SessionTokenService).rightsChanged$.subscribe(() => {
      if (this.composed) this.checkSidebarLinks(this.composedFor);
    });
    // Les fonctionnalités du pack arrivent après le premier rendu du menu :
    // il se recompose quand elles sont connues.
    this.studentFeatures.changes$.subscribe(() => {
      if (this.composed) this.checkSidebarLinks(this.composedFor);
    });
  }

  // Toggle sidebar
  toggleSidebar(): void {
    this.sidebarOpen$.next(!this.sidebarOpen$.value);
  }

  // Sidebar state as observable
  getSidebarStatus() {
    return this.sidebarOpen$.asObservable();
  }

  setSidebarStatus(value: boolean) {
    this.sidebarOpen$.next(value);
  }

  closeSidebar(): void {
    this.setSidebarStatus(false);
  }

  /**
   * Compose le menu de l'espace ouvert.
   *
   * <p>Un espace, un menu : Administration, Enseignant, Inspection, Élève ont
   * chacun le leur, et un professeur-inspecteur passe de l'un à l'autre par le
   * sélecteur d'espace au lieu de voir les entrées d'inspection greffées sur
   * son menu d'enseignant — c'est ce qui affichait deux « Bibliothèque »
   * indiscernables.</p>
   *
   * <p>Chaque entrée peut déclarer la permission qui l'ouvre, et n'apparaît que
   * si le jeton la porte : le menu ne propose jamais une destination qui
   * répondrait 403. Le garde de route et le back la redemandent — le menu
   * n'est qu'un confort, jamais une barrière.</p>
   *
   * <p>Sans espace précisé, le rôle de base tranche — c'est ce qu'attendent les
   * appelants qui ne connaissent pas l'espace courant.</p>
   */
  checkSidebarLinks(space?: Space | null): void {
    this.composedFor = space;
    this.composed = true;
    const resolved = space ?? this.defaultSpace();
    if (resolved === 'student') void this.studentFeatures.load();
    const links = resolved ? this.allowed(this.linksOf(resolved)) : [];
    this.linksSubject$.next(links);
  }

  private linksOf(space: Space): any[] {
    switch (space) {
      case 'admin': return [...ADMIN_LINKS];
      case 'teacher': return [...TEACHER_LINKS];
      case 'inspection': return [...INSPECTOR_LINKS];
      case 'student': return [...STUDENT_LINKS];
    }
  }

  private defaultSpace(): Space | null {
    if (this.authService.isAdmin()) return 'admin';
    if (this.authService.isTeacher()) return 'teacher';
    if (this.authService.isStudent()) return 'student';
    return null;
  }

  /**
   * Ne garde que ce que le jeton ouvre. Une entrée déclare la permission qui
   * l'ouvre, ou — tant que ses endpoints restent au rôle — le rôle qu'elle
   * exige ; sans l'un ni l'autre, elle est ouverte à qui a déjà l'espace.
   *
   * <p>`permission` peut être une liste : <b>l'une suffit</b>, comme pour le
   * garde de route et `[RequirePermission("a,b")]` côté serveur — c'est le
   * cas d'un écran qui s'ouvre en consultation ou en modification.</p>
   *
   * <p>Les entrées de l'espace élève déclarent aussi la fonctionnalité de pack
   * qui les ouvre (`feature`) : elle se lit sur le pack de l'élève, pas dans
   * le jeton.</p>
   *
   * <p>Le filtrage descend dans les sous-menus, et un groupe dont tous les
   * enfants sont refusés disparaît avec eux : un intitulé qui n'ouvre sur
   * rien vaut moins que pas d'intitulé du tout.</p>
   */
  private allowed(links: any[]): any[] {
    return links
      .filter((l) =>
        this.holdsOneOf(l.permission)
        && (!l.role || this.authService.hasRole(l.role))
        && this.studentFeatures.allows(l.feature))
      .map((l) => {
        if (!l.hasSubmenu) return l;
        const submenu = this.allowed(l.submenu ?? []);
        return { ...l, submenu };
      })
      .filter((l) => !l.hasSubmenu || l.submenu.length > 0);
  }

  private holdsOneOf(permission: string | string[] | undefined): boolean {
    if (!permission) return true;
    const accepted = Array.isArray(permission) ? permission : [permission];
    return accepted.some((p) => this.authService.hasPermission(p));
  }

  // 🔄 Optional getter if you don’t use async pipe
  getLinks(): any[] {
    return this.linksSubject$.getValue();
  }
}
